// InvoiceExportService.js
import * as XLSX from 'xlsx';

const InvoiceExportService = {
  /**
   * Exporter les factures en Excel avec filtrage par dates
   * @param {Array} sales - Liste des ventes/factures
   * @param {Object} options - Options d'export
   * @param {Date} options.startDate - Date de début
   * @param {Date} options.endDate - Date de fin
   * @param {Object} options.boutiqueInfo - Informations de la boutique
   * @returns {Object} - Résultat de l'export
   */
  exportInvoicesToExcel: (sales, options = {}) => {
    try {
      const { startDate, endDate, boutiqueInfo } = options;

      // Filtrer les ventes par plage de dates
      let filteredSales = sales;
      if (startDate || endDate) {
        filteredSales = sales.filter(sale => {
          const saleDate = new Date(sale.createdAt || sale.saleDate);
          const start = startDate ? new Date(startDate) : null;
          const end = endDate ? new Date(endDate) : null;

          if (start && end) {
            return saleDate >= start && saleDate <= end;
          } else if (start) {
            return saleDate >= start;
          } else if (end) {
            return saleDate <= end;
          }
          return true;
        });
      }

      if (filteredSales.length === 0) {
        return {
          success: false,
          message: 'Aucune facture trouvée pour la période sélectionnée'
        };
      }

      // Créer un nouveau workbook
      const wb = XLSX.utils.book_new();

      // ========== FEUILLE 1: RÉSUMÉ DES FACTURES ==========
      const summaryData = filteredSales.map(sale => ({
        'N° Facture': sale.saleNumber || 'N/A',
        'Date': formatDate(sale.createdAt || sale.saleDate),
        'Client': sale.customer?.name || sale.customerName || 'Client anonyme',
        'Téléphone': sale.customer?.phone || sale.customerPhone || '',
        'Montant Total': sale.total || 0,
        'Remise': sale.discount || 0,
        'Net à Payer': (sale.total || 0),
        'Mode de Paiement': getPaymentMode(sale),
        'Statut': getPaymentStatus(sale),
        'Vendeur': sale.seller?.name || sale.sellerName || 'N/A',
        'Notes': sale.notes || ''
      }));

      const ws1 = XLSX.utils.json_to_sheet(summaryData);
      
      // Styles pour la feuille résumé
      ws1['!cols'] = [
        { wch: 15 }, // N° Facture
        { wch: 18 }, // Date
        { wch: 25 }, // Client
        { wch: 15 }, // Téléphone
        { wch: 12 }, // Montant Total
        { wch: 10 }, // Remise
        { wch: 12 }, // Net à Payer
        { wch: 15 }, // Mode de Paiement
        { wch: 15 }, // Statut
        { wch: 20 }, // Vendeur
        { wch: 30 }  // Notes
      ];

      XLSX.utils.book_append_sheet(wb, ws1, 'Résumé Factures');

      // ========== FEUILLE 2: DÉTAILS DES ARTICLES ==========
      const detailsData = [];
      filteredSales.forEach(sale => {
        const items = sale.items || sale.saleItems || [];
        items.forEach(item => {
          detailsData.push({
            'N° Facture': sale.saleNumber || 'N/A',
            'Date': formatDate(sale.createdAt || sale.saleDate),
            'Client': sale.customer?.name || sale.customerName || 'Client anonyme',
            'Article': item.productName || item.product_name || item.name || 'Article',
            'Quantité': item.quantity || 1,
            'Prix Unitaire': item.unitPrice || item.unit_price || item.price || 0,
            'Total Ligne': (item.unitPrice || item.unit_price || item.price || 0) * (item.quantity || 1),
            'Vendeur': sale.seller?.name || sale.sellerName || 'N/A'
          });
        });
      });

      const ws2 = XLSX.utils.json_to_sheet(detailsData);
      
      ws2['!cols'] = [
        { wch: 15 }, // N° Facture
        { wch: 18 }, // Date
        { wch: 25 }, // Client
        { wch: 30 }, // Article
        { wch: 10 }, // Quantité
        { wch: 12 }, // Prix Unitaire
        { wch: 12 }, // Total Ligne
        { wch: 20 }  // Vendeur
      ];

      XLSX.utils.book_append_sheet(wb, ws2, 'Détails Articles');

      // ========== FEUILLE 3: STATISTIQUES ==========
      const totalVentes = filteredSales.reduce((sum, sale) => sum + (sale.total || 0), 0);
      const totalRemises = filteredSales.reduce((sum, sale) => sum + (sale.discount || 0), 0);
      const nbFactures = filteredSales.length;
      const moyenneVente = nbFactures > 0 ? totalVentes / nbFactures : 0;
      
      const ventesCredit = filteredSales.filter(s => s.isCredit && !s.isCreditPaid);
      const totalCredit = ventesCredit.reduce((sum, sale) => sum + (sale.total || 0), 0);

      const statsData = [
        { 'Indicateur': 'Période d\'export', 'Valeur': getPeriodLabel(startDate, endDate) },
        { 'Indicateur': 'Nombre de factures', 'Valeur': nbFactures },
        { 'Indicateur': 'Total des ventes', 'Valeur': `${formatCurrency(totalVentes)} XOF` },
        { 'Indicateur': 'Total des remises', 'Valeur': `${formatCurrency(totalRemises)} XOF` },
        { 'Indicateur': 'Montant net', 'Valeur': `${formatCurrency(totalVentes)} XOF` },
        { 'Indicateur': 'Moyenne par facture', 'Valeur': `${formatCurrency(moyenneVente)} XOF` },
        { 'Indicateur': 'Ventes à crédit (impayées)', 'Valeur': ventesCredit.length },
        { 'Indicateur': 'Montant total crédit impayé', 'Valeur': `${formatCurrency(totalCredit)} XOF` },
        { 'Indicateur': '', 'Valeur': '' },
        { 'Indicateur': 'Boutique', 'Valeur': boutiqueInfo?.name || 'SmartBoutik' },
        { 'Indicateur': 'Date d\'export', 'Valeur': new Date().toLocaleString('fr-FR') }
      ];

      const ws3 = XLSX.utils.json_to_sheet(statsData);
      ws3['!cols'] = [{ wch: 30 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(wb, ws3, 'Statistiques');

      // ========== GÉNÉRATION DU FICHIER ==========
      const fileName = generateFileName(startDate, endDate);
      XLSX.writeFile(wb, fileName);

      return {
        success: true,
        message: `Export réussi ! ${nbFactures} factures exportées`,
        fileName: fileName,
        stats: {
          nbFactures,
          totalVentes,
          totalRemises,
          moyenneVente
        }
      };

    } catch (error) {
      console.error('❌ Erreur export Excel:', error);
      return {
        success: false,
        message: `Erreur lors de l'export: ${error.message}`
      };
    }
  },

  /**
   * Exporter une seule facture détaillée en Excel
   * @param {Object} sale - La vente/facture à exporter
   * @param {Object} boutiqueInfo - Informations de la boutique
   */
  exportSingleInvoice: (sale, boutiqueInfo = {}) => {
    try {
      const wb = XLSX.utils.book_new();

      // En-tête de facture
      const headerData = [
        ['FACTURE'],
        [''],
        ['Boutique:', boutiqueInfo?.name || 'SmartBoutik'],
        ['Adresse:', boutiqueInfo?.address || ''],
        ['Téléphone:', boutiqueInfo?.phone || ''],
        ['Email:', boutiqueInfo?.email || ''],
        [''],
        ['N° Facture:', sale.saleNumber || 'N/A'],
        ['Date:', formatDate(sale.createdAt || sale.saleDate)],
        ['Vendeur:', sale.seller?.name || sale.sellerName || 'N/A'],
        [''],
        ['Client:', sale.customer?.name || sale.customerName || 'Client anonyme'],
        ['Téléphone:', sale.customer?.phone || sale.customerPhone || ''],
        ['Adresse:', sale.customer?.address || sale.customerAddress || ''],
        ['']
      ];

      // Articles
      const items = sale.items || sale.saleItems || [];
      const itemsData = items.map(item => ({
        'Description': item.productName || item.product_name || item.name || 'Article',
        'Quantité': item.quantity || 1,
        'Prix Unitaire': item.unitPrice || item.unit_price || item.price || 0,
        'Total': (item.unitPrice || item.unit_price || item.price || 0) * (item.quantity || 1)
      }));

      const ws = XLSX.utils.aoa_to_sheet(headerData);
      XLSX.utils.sheet_add_json(ws, itemsData, { origin: -1 });

      // Totaux
      const subtotal = (sale.total || 0) + (sale.discount || 0);
      const totalsData = [
        [''],
        ['Sous-total:', subtotal],
        ['Remise:', -(sale.discount || 0)],
        ['TOTAL À PAYER:', sale.total || 0],
        [''],
        ['Mode de paiement:', getPaymentMode(sale)],
        ['Statut:', getPaymentStatus(sale)]
      ];

      XLSX.utils.sheet_add_aoa(ws, totalsData, { origin: -1 });

      if (sale.notes) {
        XLSX.utils.sheet_add_aoa(ws, [[''], ['Notes:', sale.notes]], { origin: -1 });
      }

      ws['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];

      XLSX.utils.book_append_sheet(wb, ws, 'Facture');

      const fileName = `Facture_${sale.saleNumber || 'N/A'}_${new Date().getTime()}.xlsx`;
      XLSX.writeFile(wb, fileName);

      return {
        success: true,
        message: `Facture ${sale.saleNumber} exportée avec succès`,
        fileName
      };

    } catch (error) {
      console.error('❌ Erreur export facture:', error);
      return {
        success: false,
        message: `Erreur lors de l'export: ${error.message}`
      };
    }
  }
};

// ========== FONCTIONS UTILITAIRES ==========

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('fr-FR').format(amount || 0);
}

function getPaymentMode(sale) {
  if (sale.isCredit) {
    return 'À crédit';
  }
  return sale.paymentMethod || 'Espèces';
}

function getPaymentStatus(sale) {
  if (sale.isCredit && !sale.isCreditPaid) {
    return 'Crédit impayé';
  }
  if (sale.isCredit && sale.isCreditPaid) {
    return 'Crédit soldé';
  }
  return 'Payé';
}

function getPeriodLabel(startDate, endDate) {
  if (startDate && endDate) {
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  } else if (startDate) {
    return `À partir du ${formatDate(startDate)}`;
  } else if (endDate) {
    return `Jusqu'au ${formatDate(endDate)}`;
  }
  return 'Toutes les périodes';
}

function generateFileName(startDate, endDate) {
  const timestamp = new Date().getTime();
  let period = '';
  
  if (startDate && endDate) {
    const start = new Date(startDate).toLocaleDateString('fr-FR').replace(/\//g, '-');
    const end = new Date(endDate).toLocaleDateString('fr-FR').replace(/\//g, '-');
    period = `_${start}_au_${end}`;
  } else if (startDate) {
    period = `_depuis_${new Date(startDate).toLocaleDateString('fr-FR').replace(/\//g, '-')}`;
  } else if (endDate) {
    period = `_jusque_${new Date(endDate).toLocaleDateString('fr-FR').replace(/\//g, '-')}`;
  }
  
  return `Factures${period}_${timestamp}.xlsx`;
}

export default InvoiceExportService;